import { Button } from '@material-ui/core';
import clsx from 'clsx';
import { FC } from 'react';
import { valueFormated } from '../formatValue';
import { useStyles } from './style';

interface ITableNormal {
  data: any[];
  header: any[];
  width: string;
  bgColor: string;
  btnClick: (field: string, data: any) => (e: any) => void;
  variant?: 'default' | 'hierarchy';
}

const SERVICE_BADGE_COLORS: Record<number, { background: string; color: string }> = {
  1: { background: '#F8D7E8', color: '#9B1B5C' },
  2: { background: '#F8E7A0', color: '#8A6D00' },
  3: { background: '#F8D5C2', color: '#9A4B1C' },
  4: { background: '#D8F0C8', color: '#3F6B1D' },
  5: { background: '#E3D7F5', color: '#5B3A8C' },
  6: { background: '#D6E8F8', color: '#1F4E79' },
};

const DEFAULT_BADGE_COLOR = { background: '#E8EAF0', color: '#0E1247' };

const getServiceBadgeStyle = (serviceId?: number) =>
  (serviceId && SERVICE_BADGE_COLORS[serviceId]) || DEFAULT_BADGE_COLOR;

const getServiceName = (service: any) =>
  service?.name || service?.attributes?.name || `Service ${service?.id || ''}`;

/** Merge primary `service` + multi `services` (beneficiary prestations). */
const getUserPrestationServices = (row: any) => {
  const byId = new Map<number, any>();

  const addService = (service: any) => {
    if (service == null) {
      return;
    }

    const id =
      typeof service === 'object' ? Number(service.id) : Number(service);
    if (!id || Number.isNaN(id)) {
      return;
    }

    if (!byId.has(id)) {
      byId.set(
        id,
        typeof service === 'object'
          ? service
          : { id, name: `Service ${id}` },
      );
    }
  };

  addService(row?.service);
  (Array.isArray(row?.services) ? row.services : []).forEach(addService);

  return Array.from(byId.values());
};

const TableNormal: FC<ITableNormal> = ({
  data,
  header,
  width,
  bgColor,
  btnClick,
  variant = 'default',
}) => {
  const classes = useStyles();
  const isHierarchy = variant === 'hierarchy';

  const renderUserIdentity = (row: any, item: any, index: number) => {
    const firstname = row?.firstname || '';
    const lastname = row?.lastname || '';
    const fullName = `${firstname} ${lastname}`.trim() || '—';

    return (
      <div
        className={clsx(classes.content, isHierarchy && classes.consultantContent)}
        key={`userIdentity-${item.key}-${index}`}>
        <div className={classes.userIdentity}>
          <button type="button" className={classes.userName} onClick={btnClick('firstname', row)}>
            {fullName}
          </button>
          <span className={classes.userEmail}>{row?.email || ''}</span>
        </div>
      </div>
    );
  };

  const renderServiceBadges = (row: any, item: any, index: number) => {
    const source = item?.option?.source;
    let services: any[] = [];

    if (source === 'userServices') {
      services = getUserPrestationServices(row);
    } else {
      const fromAllowed = Array.isArray(row?.allowed_services) ? row.allowed_services : [];
      const fromServices = Array.isArray(row?.services) ? row.services : [];
      services = fromAllowed.length ? fromAllowed : fromServices;
    }

    const maxVisible = item?.option?.maxVisible || 3;
    const visibleServices = services.slice(0, maxVisible);
    const hiddenCount = Math.max(services.length - maxVisible, 0);

    return (
      <div
        className={clsx(classes.content, isHierarchy && classes.consultantContent)}
        key={`serviceBadges-${item.key}-${index}`}>
        <div className={classes.badgesRow}>
          {visibleServices.map((service: any) => {
            const colors = getServiceBadgeStyle(service?.id);
            return (
              <span
                key={`${item.key}-${index}-${service?.id || getServiceName(service)}`}
                className={classes.serviceBadge}
                style={{ backgroundColor: colors.background, color: colors.color }}>
                {getServiceName(service)}
              </span>
            );
          })}
          {hiddenCount > 0 && <span className={classes.moreBadge}>+{hiddenCount}</span>}
          {!services.length && <span className={classes.emptyValue}>—</span>}
        </div>
      </div>
    );
  };

  const renderStatusBadge = (row: any, item: any, index: number) => {
    const isActive = Boolean(row?.[item.key]);
    return (
      <div
        className={clsx(classes.content, isHierarchy && classes.consultantContent)}
        key={`statusBadge-${item.key}-${index}`}>
        <span
          className={clsx(classes.statusBadge, {
            [classes.statusActive]: isActive,
            [classes.statusInactive]: !isActive,
          })}>
          {isActive ? 'Actif' : 'Inactif'}
        </span>
      </div>
    );
  };

  return (
    <div className={classes.root} style={{ width }}>
      {(header || []).map((item: any) => {
        return (
          <div
            key={item.key}
            className={classes.coulomContent}
            style={{
              minWidth: item?.width,
              flex: isHierarchy ? `1 1 ${item?.width || 'auto'}` : undefined,
            }}>
            <div
              className={clsx({
                [classes.header]: true,
                [classes.headerTextCenter]: item?.option?.type === 'button',
              })}
              style={{ backgroundColor: bgColor }}>
              {item.label}
            </div>

            {(data || []).map((x: any, index: number) => {
              if (item?.option?.type === 'userIdentity') {
                return renderUserIdentity(x, item, index);
              }

              if (item?.option?.type === 'serviceBadges') {
                return renderServiceBadges(x, item, index);
              }

              if (item?.option?.type === 'statusBadge') {
                return renderStatusBadge(x, item, index);
              }

              if (item?.option?.type === 'button') {
                return (
                  <div
                    className={clsx(classes.contentBtn, {
                      [classes.consultantContentBtn]: isHierarchy,
                    })}
                    key={`${item?.option?.type}-${item.key}-${index.toString()}`}>
                    <Button
                      onClick={btnClick(item.key, x)}
                      className={clsx(classes.button, {
                        [classes.consultantButton]: isHierarchy,
                      })}
                      data-cy={`${item?.option?.type}-${item.key}-${index}`}>
                      {item?.option?.icon || item?.option?.labelText}
                    </Button>
                  </div>
                );
              }

              if (item?.option?.type === 'link') {
                return (
                  <div
                    className={classes.content}
                    key={`${item?.option?.type}-${item.key}-${index.toString()}`}>
                    <div onClick={btnClick(item.key, x)} className={classes.link}>
                      {valueFormated(x, item.key, item.option)}
                    </div>
                  </div>
                );
              }

              const value = valueFormated(x, item.key, item.option);

              return (
                <div
                  className={classes.content}
                  key={`${item?.option?.type}-${item.key}-${index.toString()}`}>
                  {value}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default TableNormal;
